import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { companyAPI, platformAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { User, Lock, ArrowRight, Shield, AlertTriangle, Users, BarChart3, Settings } from 'lucide-react';

/**
 * Detect if a user is a platform super admin.
 */
function isSuperAdmin(user) {
  if (!user) return false;
  if (user.company_id) return false;
  if (user.id === 1) return true;
  if (['Administrator', 'Superadmin'].includes(user.role_name)) return true;
  return false;
}

export default function SuperAdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleStatus, setRoleStatus] = useState(null); // 'superadmin' | 'not-superadmin' | null
  const [companyLogo, setCompanyLogo] = useState(null);
  const [platformLogo, setPlatformLogo] = useState(null);

  useState(() => {
    platformAPI.getPublicSettings().then(data => {
      if (data.logo_path) setPlatformLogo(data.logo_path);
    }).catch(() => {});
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRoleStatus(null);
    setCompanyLogo(null);
    try {
      const data = await login(username, password);
      const user = data.user;

      // Fetch company logo if this is a company user (non-superadmin)
      if (!isSuperAdmin(user) && user.company_id) {
        try {
          const logoData = await companyAPI.getLogo(user.company_id);
          if (logoData.logo_path) {
            setCompanyLogo(fileUrl(logoData.logo_path));
          }
        } catch { /* logo fetch failed, continue */ }
      }

      if (isSuperAdmin(user)) {
        setRoleStatus('superadmin');
        toast.success('Welcome back, Admin!');
        setTimeout(() => navigate('/super-admin'), 800);
      } else {
        setRoleStatus('not-superadmin');
        toast.error('This account is not a Platform Admin. Redirecting to Company Login...');
        // Log out and redirect to company login
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-login-layout">
      {/* ========== LEFT — PREMIUM DARK GRADIENT BRANDING PANEL ========== */}
      <div className="sa-login-brand">
        <div className="sa-login-brand-content">
          {/* Shield Icon */}
          {platformLogo ? (
            <img src={fileUrl(platformLogo)} alt="Logo" style={{ width: 72, height: 72, borderRadius: 20, objectFit: 'contain', boxShadow: '0 12px 32px rgba(0,0,0,0.4)', marginBottom: 24 }} />
          ) : (
            <div className="sa-brand-shield">
              <Shield size={36} />
            </div>
          )}

          {/* Headline */}
          <h2>
            Platform <span className="accent">Admin Portal</span>
          </h2>
          <p>
            Manage companies, subscriptions, and platform settings from a
            centralized administration dashboard.
          </p>

          {/* Feature highlights */}
          <div className="sa-brand-features">
            <div className="sa-brand-feature">
              <div className="sa-brand-feature-icon red">
                <Users size={18} />
              </div>
              <div className="sa-brand-feature-text">
                <h4>User Management</h4>
                <p>Oversee all platform users and companies</p>
              </div>
            </div>
            <div className="sa-brand-feature">
              <div className="sa-brand-feature-icon indigo">
                <BarChart3 size={18} />
              </div>
              <div className="sa-brand-feature-text">
                <h4>Analytics & Reports</h4>
                <p>Monitor platform health and usage metrics</p>
              </div>
            </div>
            <div className="sa-brand-feature">
              <div className="sa-brand-feature-icon amber">
                <Settings size={18} />
              </div>
              <div className="sa-brand-feature-text">
                <h4>Platform Settings</h4>
                <p>Configure plans, pricing, and system options</p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="sa-brand-trust">
            <div className="sa-trust-item">
              <Shield size={14} />
              Restricted Access
            </div>
            <div className="sa-trust-item">
              <Lock size={14} />
              Encrypted Connection
            </div>
          </div>
        </div>
      </div>

      {/* ========== RIGHT — FORM PANEL ========== */}
      <div className="sa-login-form-panel">
        <div className="sa-login-form-wrapper">
          <div className="sa-login-card">
            <div className="sa-login-card-header">
              <div className="sa-login-logo">
                {companyLogo ? (
                  <img src={companyLogo} alt="Company Logo" className="sa-login-logo-img" style={{ objectFit: 'contain' }} />
                ) : platformLogo ? (
                  <img src={fileUrl(platformLogo)} alt="Platform Logo" className="sa-login-logo-img" style={{ objectFit: 'contain' }} />
                ) : (
                  <img src="/favicon.svg" alt="LedgerPro" className="sa-login-logo-img" />
                )}
              </div>
              <div className="sa-login-shield">
                <Shield size={20} />
              </div>
              <h2>Platform Admin</h2>
              <p>Sign in to LedgerPro Admin Portal</p>
            </div>
            <div className="sa-login-card-body">
              <form onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label>Username or Email</label>
                  <div className="auth-input-icon">
                    <User size={18} className="auth-input-icon-svg" />
                    <input
                      className="auth-input"
                      type="text"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      required
                      placeholder="Enter admin username"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="auth-field">
                  <label>Password</label>
                  <div className="auth-input-icon">
                    <Lock size={18} className="auth-input-icon-svg" />
                    <input
                      className="auth-input"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="Enter admin password"
                    />
                  </div>
                </div>
                <button className="auth-submit sa-login-submit" type="submit" disabled={loading || roleStatus === 'not-superadmin'}>
                  {loading ? (
                    <>
                      <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                      Signing in...
                    </>
                  ) : roleStatus === 'superadmin' ? (
                    <>
                      <Shield size={16} />
                      Welcome, Admin!
                    </>
                  ) : roleStatus === 'not-superadmin' ? (
                    <>
                      <AlertTriangle size={16} />
                      Access Denied
                    </>
                  ) : (
                    <>
                      Sign In to Admin Portal
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              {/* Role detection indicator */}
              {roleStatus === 'superadmin' && (
                <div className="auth-role-indicator" style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#166534',
                }}>
                  <Shield size={16} /> Platform Admin verified — Redirecting...
                </div>
              )}
              {roleStatus === 'not-superadmin' && (
                <div className="auth-role-indicator" style={{
                  marginTop: 12,
                  padding: '10px 14px',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                }}>
                  <AlertTriangle size={16} /> This account is not a Platform Admin — use Company Login instead
                </div>
              )}

              {!roleStatus && (
                <div className="sa-login-info">
                  <Shield size={14} />
                  <span>Restricted to Platform Administrators only</span>
                </div>
              )}
            </div>
          </div>

          <div className="sa-login-footer">
            <Link to="/login" className="sa-login-company-link">
              ← Go to Company Login
            </Link>
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>
              © 2026 LedgerPro. Platform Administration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
